import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, UserCircle, Shield, HelpCircle, Info, MessageSquare } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import logoDark from '../assets/images/hande_logo_dark.png';
import logoLight from '../assets/images/hande_logo_light.png';

interface HeaderProps {
  title?: string;
  showBackButton?: boolean;
  onBack?: () => void;
}

const Header: React.FC<HeaderProps> = ({ title }) => {
  const { user, logout } = useAuth();
  const { themeClasses, isLightTheme } = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleMenuClick = (action: string) => {
    setShowDropdown(false);
    switch (action) {
      case 'profile':
        navigate('/profile');
        break;
      case 'privacy':
        navigate('/privacy-policy');
        break;
      case 'help':
        toast('Help feature coming soon!', { icon: 'ℹ️' });
        break;
      case 'about':
        navigate('/about');
        break;
      case 'faq':
        navigate('/faq');
        break;
      case 'logout':
        logout();
        navigate('/');
        break;
    }
  };


  return (
    <div className={`fixed top-0 left-0 right-0 ${themeClasses.card} border-b ${themeClasses.border} z-40 pb-0`}>
      <div className="flex items-center justify-between p-4 relative">
        {/* Left section - Logo */}
        <div className="flex items-center w-1/3 absolute left-0 -top-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center transition-opacity hover:opacity-80 relative"
          >
            <img
              src={isLightTheme ? logoDark : logoLight}
              alt="Startup Hande"
              className="h-32 w-auto"
            />
          </button>
        </div>
        <div className="flex items-center w-1/3"></div>
        
        {/* Center section - Title */}
        <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center w-1/3">
          {title && (
            <h1 className={`text-lg font-semibold ${themeClasses.text} text-center`}>{title}</h1>
          )}
          {!title && (
            <span className={`${themeClasses.textSecondary} text-sm text-center`}>Welcome {user?.firstName} {user?.lastName}!</span>
          )}
        </div>
        
        {/* Right section - Avatar */}
        <div className="flex items-center justify-end w-1/3">
          <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className={`w-8 h-8 ${isLightTheme ? 'bg-gradient-to-r from-cyan-200 to-blue-200' : 'bg-gray-600'} rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary-500`}
          >
            {user?.profilePictureUrl ? (
              <img src={user.profilePictureUrl} alt={user.firstName} className="w-full h-full rounded-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className={`w-5 h-5 ${isLightTheme ? 'text-gray-600' : 'text-gray-300'}`} />
              </div>
            )}
          </button>
          
          {showDropdown && (
            <div className={`absolute right-0 mt-2 w-56 ${themeClasses.card} ${themeClasses.border} border rounded-lg shadow-lg z-50 overflow-hidden`}>
              <div className="py-1">
                <button
                  onClick={() => handleMenuClick('profile')}
                  className={`w-full text-left px-4 py-2 ${themeClasses.text} hover:${isLightTheme ? 'bg-gray-100' : 'bg-gray-700'} flex items-center transition-colors`}
                >
                  <UserCircle className="w-4 h-4 mr-3" />
                  Profile
                </button>
                <button
                  onClick={() => handleMenuClick('privacy')}
                  className={`w-full text-left px-4 py-2 ${themeClasses.text} hover:${isLightTheme ? 'bg-gray-100' : 'bg-gray-700'} flex items-center transition-colors`}
                >
                  <Shield className="w-4 h-4 mr-3" />
                  Privacy Policy
                </button>
                <button
                  onClick={() => handleMenuClick('help')}
                  className={`w-full text-left px-4 py-2 ${themeClasses.text} hover:${isLightTheme ? 'bg-gray-100' : 'bg-gray-700'} flex items-center transition-colors`}
                >
                  <HelpCircle className="w-4 h-4 mr-3" />
                  Help
                </button>
                <button
                  onClick={() => handleMenuClick('about')}
                  className={`w-full text-left px-4 py-2 ${themeClasses.text} hover:${isLightTheme ? 'bg-gray-100' : 'bg-gray-700'} flex items-center transition-colors`}
                >
                  <Info className="w-4 h-4 mr-3" />
                  About
                </button>
                <button
                  onClick={() => handleMenuClick('faq')}
                  className={`w-full text-left px-4 py-2 ${themeClasses.text} hover:${isLightTheme ? 'bg-gray-100' : 'bg-gray-700'} flex items-center transition-colors`}
                >
                  <MessageSquare className="w-4 h-4 mr-3" />
                  Frequent Questions
                </button>
                <div className={`border-t ${themeClasses.border} my-1`}></div>
                <button
                  onClick={() => handleMenuClick('logout')}
                  className={`w-full text-left px-4 py-2 ${isLightTheme ? 'text-red-600 hover:bg-red-50' : 'text-red-400 hover:bg-red-900/20'} flex items-center transition-colors`}
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Logout
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;

